import { useCallback } from 'react';
import { useWatch, type Control, type UseFormSetValue } from 'react-hook-form';

import type { ChecklistEvaluationKey, EvaluationParams, FinanceType, ToleranceBound } from 'types';

import { DialogFormValues } from './checklistItemInputOptions';
import { evaluationTemplateRegistry } from './evaluationTemplateRegistry';

export interface EvaluationParamsEditorProps {
  inputIndex: number;
  control: Control<DialogFormValues>;
  setValue: UseFormSetValue<DialogFormValues>;
  evaluationKey: ChecklistEvaluationKey | undefined;
}

const isEmptyBound = (bound: ToleranceBound | undefined): boolean =>
  !bound || (bound.minPct === undefined && bound.maxPct === undefined);

// Strips empty bounds/maps so a fully-blank editor collapses to `undefined` rather than an
// object of `undefined`s — preserves mergeEvaluationParams's fallback to descriptor defaults.
export const normalizeEvaluationParams = (params: EvaluationParams): EvaluationParams | undefined => {
  const next: EvaluationParams = {};

  if (params.tolerancesByFinanceType) {
    const cleaned: NonNullable<EvaluationParams['tolerancesByFinanceType']> = {};
    for (const [key, bound] of Object.entries(params.tolerancesByFinanceType)) {
      if (isEmptyBound(bound)) continue;
      cleaned[key as FinanceType | 'default'] = bound;
    }
    if (Object.keys(cleaned).length > 0) next.tolerancesByFinanceType = cleaned;
  }

  if (params.toleranceDegrees !== undefined) next.toleranceDegrees = params.toleranceDegrees;
  if (params.minDegrees !== undefined) next.minDegrees = params.minDegrees;
  if (params.maxDegrees !== undefined) next.maxDegrees = params.maxDegrees;

  return Object.keys(next).length > 0 ? next : undefined;
};

// Stable identity for the "no params yet" case so useCallback deps below don't see a new
// object on every render when the watched field is undefined.
const EMPTY_PARAMS: EvaluationParams = {};

// Template-authored override of a checklist evaluation's tolerance bounds, gated on evaluationKey
// since each key's `defaultParams` has a different shape (see ChecklistEvaluationSystemSize/
// ArrayPitch in types). Leaving every field blank must resolve to `evaluationParams: undefined`
// so the server's mergeEvaluationParams falls through to the descriptor defaults.
export const EvaluationParamsEditor = ({
  inputIndex,
  control,
  setValue,
  evaluationKey,
}: EvaluationParamsEditorProps) => {
  const params = useWatch({ control, name: `inputs.${inputIndex}.evaluationParams` }) ?? EMPTY_PARAMS;

  const handleCommit = useCallback(
    (next: EvaluationParams) => {
      setValue(`inputs.${inputIndex}.evaluationParams`, normalizeEvaluationParams(next));
    },
    [setValue, inputIndex],
  );

  const descriptor = evaluationKey ? evaluationTemplateRegistry[evaluationKey] : undefined;
  if (!descriptor) return null;

  const { ParamsEditor } = descriptor;
  return <ParamsEditor params={params} onCommit={handleCommit} />;
};
